/*
 * Menu desktop (snippets/ec-header-dropdown-menu.liquid).
 *
 * Abrir no hover e fechar ao sair continuam com o HeaderMenu (details-disclosure.js). Aqui
 * ficam:
 * - adiantar as imagens dos painéis quando o mouse chega na faixa;
 * - fechar o painel anterior assim que outro abre (o HeaderMenu só fecha depois de 400 ms);
 * - manter o painel dentro da tela: desloca para a esquerda e limita a altura;
 * - os selos de desconto automáticos das categorias do "Todos", com os mesmos dados e o
 *   mesmo cache do menu mobile (ec-mobile-menu.js);
 * - a troca de tamanho e o "Comprar agora" do produto em destaque.
 */
if (!customElements.get('ec-desktop-menu')) {
  const EC_DM_BADGES_TTL = 10 * 60 * 1000;
  const EC_DM_GUTTER = 16;

  customElements.define(
    'ec-desktop-menu',
    class EcDesktopMenu extends HTMLElement {
      connectedCallback() {
        this.badgesUrl = this.dataset.badgesUrl;
        this.badgesRequested = false;
        this.resizeFrame = null;

        this.onToggle = this.onToggle.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onResize = this.onResize.bind(this);
        this.warmUp = this.warmUp.bind(this);

        // "toggle" não borbulha, mas passa por aqui na fase de captura.
        this.addEventListener('toggle', this.onToggle, true);
        this.addEventListener('click', this.onClick);
        this.addEventListener('pointerover', this.warmUp, { once: true });
        this.addEventListener('focusin', this.warmUp, { once: true });
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        this.removeEventListener('toggle', this.onToggle, true);
        this.removeEventListener('pointerover', this.warmUp);
        this.removeEventListener('focusin', this.warmUp);
        this.removeEventListener('click', this.onClick);
        window.removeEventListener('resize', this.onResize);
      }

      // As imagens dos painéis são lazy e ficam num <details> fechado, então só carregariam
      // quando o painel abre. Passar para eager quando o mouse chega na faixa adianta a carga
      // sem pesar nas páginas em que ninguém usa o menu.
      warmUp() {
        this.removeEventListener('pointerover', this.warmUp);
        this.removeEventListener('focusin', this.warmUp);
        this.querySelectorAll('[data-ec-dm-panel] img[loading="lazy"]').forEach((image) => {
          image.loading = 'eager';
        });
      }

      onToggle(event) {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open) return;

        this.querySelectorAll('details[open]').forEach((other) => {
          if (other === details) return;
          const disclosure = other.closest('header-menu');
          if (disclosure?.close) disclosure.close();
          else other.removeAttribute('open');
        });

        const panel = details.querySelector('[data-ec-dm-panel]');
        if (!panel) return;
        this.place(details, panel);
        if (panel.querySelector('[data-ec-dm-badge]')) this.loadBadges();
      }

      onResize() {
        if (this.resizeFrame) return;
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = null;
          const details = this.querySelector('details[open]');
          const panel = details?.querySelector('[data-ec-dm-panel]');
          if (panel) this.place(details, panel);
        });
      }

      // O painel começa alinhado ao item da faixa; o que passar da borda direita da tela
      // vira deslocamento para a esquerda, e a altura fica limitada ao que sobra da janela.
      // À esquerda o limite é a própria tela: o "Todos" encosta nela junto com o fundo do item.
      place(details, panel) {
        panel.style.removeProperty('--ec-dm-shift');
        const viewport = document.documentElement.clientWidth;
        const rect = panel.getBoundingClientRect();

        let shift = 0;
        if (rect.right > viewport - EC_DM_GUTTER) shift = viewport - EC_DM_GUTTER - rect.right;
        if (rect.left + shift < 0) shift = -rect.left;
        panel.style.setProperty('--ec-dm-shift', `${Math.round(shift)}px`);

        const top = details.getBoundingClientRect().bottom;
        const maxHeight = Math.max(240, Math.floor(window.innerHeight - top - EC_DM_GUTTER));
        panel.style.setProperty('--ec-dm-max-height', `${maxHeight}px`);
      }

      onClick(event) {
        const option = event.target.closest('[data-ec-dm-option]');
        if (option && this.contains(option)) {
          this.selectOption(option);
          return;
        }

        const buy = event.target.closest('[data-ec-dm-buy]');
        if (buy && this.contains(buy)) this.buy(buy);
      }

      // Selos automáticos

      loadBadges() {
        if (!this.badgesUrl || this.badgesRequested) return;
        this.badgesRequested = true;

        const cached = this.readCachedBadges();
        if (cached) {
          this.applyBadges(cached);
          return;
        }

        fetch(this.badgesUrl)
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const data = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ec-mm-discounts]');
            if (!data) return;
            const discounts = JSON.parse(data.textContent);
            this.writeCachedBadges(discounts);
            this.applyBadges(discounts);
          })
          .catch(() => {
            // Sem selos desta vez; a próxima abertura tenta de novo.
            this.badgesRequested = false;
          });
      }

      // discounts: { "<url do link>": <maior desconto %> }. A categoria mostra o maior valor
      // entre os subitens dela; sem subitens, o dela mesma.
      applyBadges(discounts) {
        this.querySelectorAll('[data-ec-dm-badge]').forEach((slot) => {
          const category = slot.closest('.ec-dm__category');
          if (!category) return;

          let links = category.querySelectorAll('.ec-dm__category-link');
          if (!links.length) links = category.querySelectorAll('.ec-dm__category-head');
          const values = Array.from(links, (link) => discounts[link.getAttribute('href')] || 0);
          const discount = Math.max(0, ...values);

          slot.querySelector(':scope > .ec-dm__badge')?.remove();
          if (!(discount > 0)) return;

          // A cor vem do bloco da categoria e independe de o campo "Selo" estar preenchido.
          const badge = document.createElement('span');
          badge.className = `ec-dm__badge ec-dm__badge--${slot.dataset.ecDmBadge || 'amarelo'}`;
          badge.textContent = `-${discount}%`;
          slot.append(badge);
        });
      }

      // Mesma chave do ec-mobile-menu.js: a resposta de um menu serve para o outro.
      badgesCacheKey() {
        return `ec-mm-badges:${this.badgesUrl}`;
      }

      readCachedBadges() {
        try {
          const cached = JSON.parse(sessionStorage.getItem(this.badgesCacheKey()));
          if (cached && Date.now() - cached.savedAt < EC_DM_BADGES_TTL) return cached.discounts;
        } catch (error) {
          // sessionStorage indisponível (aba anônima, bloqueio): segue sem cache.
        }
        return null;
      }

      writeCachedBadges(discounts) {
        try {
          sessionStorage.setItem(this.badgesCacheKey(), JSON.stringify({ savedAt: Date.now(), discounts }));
        } catch (error) {
          // Idem: sem cache, o menu busca de novo na próxima página.
        }
      }

      // Produto em destaque

      selectOption(button) {
        if (button.disabled) return;
        const product = button.closest('[data-ec-dm-product]');
        const entry = this.productOptions(product)[Number(button.dataset.ecDmOption)];
        if (!entry) return;

        product.querySelectorAll('[data-ec-dm-option]').forEach((other) => {
          other.setAttribute('aria-pressed', String(other === button));
        });

        product.querySelectorAll('[data-ec-dm-product-link]').forEach((link) => {
          link.href = entry.url;
        });

        const image = product.querySelector('[data-ec-dm-product-image]');
        if (image && entry.image) {
          image.src = entry.image;
          image.alt = entry.title;
        }

        const setText = (selector, text) => {
          const element = product.querySelector(selector);
          if (element) element.textContent = text;
        };
        setText('[data-ec-dm-product-vendor]', entry.vendor);
        setText('[data-ec-dm-product-title]', entry.title);
        setText('[data-ec-dm-product-price]', entry.price);
        setText('[data-ec-dm-product-pix]', entry.pix);
        setText('[data-ec-dm-product-installment]', entry.installment);

        const compare = product.querySelector('[data-ec-dm-product-compare]');
        if (compare) {
          compare.textContent = entry.compare;
          compare.hidden = !entry.compare;
        }

        const discount = product.querySelector('[data-ec-dm-product-discount]');
        if (discount) {
          discount.textContent = `-${entry.discount}%`;
          discount.hidden = !(entry.discount > 0);
        }

        const buy = product.querySelector('[data-ec-dm-buy]');
        if (buy) {
          buy.dataset.variantId = entry.variantId;
          buy.disabled = !entry.available;
          buy.textContent = entry.available ? 'Comprar agora' : 'Esgotado';
        }
      }

      productOptions(product) {
        if (!product.ecDmOptions) {
          try {
            product.ecDmOptions = JSON.parse(product.querySelector('[data-ec-dm-options]')?.textContent || '[]');
          } catch (error) {
            product.ecDmOptions = [];
          }
        }
        return product.ecDmOptions;
      }

      // Como o ec-buy-now-button da página de produto: põe no carrinho e vai ao checkout.
      // Se o carrinho recusar, abre a página do produto para a compra seguir por lá.
      buy(button) {
        const product = button.closest('[data-ec-dm-product]');
        const variantId = Number(button.dataset.variantId);
        if (!product || !variantId || button.disabled) return;

        button.disabled = true;
        button.classList.add('is-loading');

        fetch(`${product.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status) throw new Error(data.description || data.message);
            window.location.href = product.dataset.checkoutUrl;
          })
          .catch((error) => {
            console.error('[ec-desktop-menu]', error);
            button.disabled = false;
            button.classList.remove('is-loading');
            const link = product.querySelector('[data-ec-dm-product-link]');
            if (link) window.location.href = link.href;
          });
      }
    }
  );
}
